import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { verifyPasswordResetCode, confirmPasswordReset } from 'firebase/auth';
import { auth } from './firebase';

function ResetPassword() {
  const [searchParams] = useSearchParams(); // To get query parameters from the URL
  const [isValid, setIsValid] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [passwordStrength, setPasswordStrength] = useState({ score: 0, label: "Very Weak" });
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [isPasswordVisible, setIsPasswordVisible] = useState(false);
  const [isConfirmPasswordVisible, setIsConfirmPasswordVisible] = useState(false);
  const [confirmPassword, setConfirmPassword] = useState("");
  const [doPasswordsMatch, setDoPasswordsMatch] = useState(false);

  const oobCode = searchParams.get('oobCode'); // Extract the oobCode from the URL
  const mode = searchParams.get('mode'); // Extract the mode from the URL
  const isNewUser = searchParams.get("newuser") === "true"

  useEffect(() => {
    // Validate the oobCode when the page loads
    if (mode === 'resetPassword' && oobCode) {
      verifyPasswordResetCode(auth, oobCode)
        .then(() => {
          setIsValid(true); // If valid, show the password reset form
        })
        .catch(() => {
          setError('Invalid or expired password reset link.');
        });
    } else {
      setError('Invalid request.');
    }
  }, [mode, oobCode]);

  const handlePasswordReset = async () => {
    setError('');

    // Check if passwords match
    if (newPassword !== confirmPassword) {
        setError("Passwords do not match. Please try again.");
        return;
    }

    if (!newPassword) {
      setError('Please enter a new password.');
      return;
    }

    // Check password strength before submitting
    const passwordValidation = validatePassword(newPassword);
    if (passwordValidation !== 'Strong') {
      setError('Password must be strong: include uppercase, lowercase, numbers, and special characters.');
      return;
    }

    try {
      await confirmPasswordReset(auth, oobCode, newPassword);
      setSuccessMessage(
        <>
          Your password has been reset successfully! You can now <a href="https://app.proactr.com/login" target="_blank" rel="noopener noreferrer">click here to log in</a> with your new password.
        </>
      );
      setIsValid(false); // Hide the form after success
    } catch (err) {
      setError('Failed to reset your password. Please try again.');
    }
  };

  const validatePassword = (password) => {
    const hasLowercase = /[a-z]/.test(password);
    const hasUppercase = /[A-Z]/.test(password);
    const hasNumber = /\d/.test(password);
    const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>-]/.test(password);
    const lengthValid = password.length >= 8;

    if (lengthValid && hasLowercase && hasUppercase && hasNumber && hasSpecialChar) {
      return 'Strong';
    } else if (lengthValid && (hasLowercase + hasUppercase + hasNumber + hasSpecialChar >= 3)) {
      return 'Moderate';
    } else {
      return 'Weak';
    }
  };

  const handlePasswordChange = (e) => {
    const password = e.target.value;
    setNewPassword(password);
    const strength = calculatePasswordStrength(password);
    setPasswordStrength(strength);
    setDoPasswordsMatch(password === confirmPassword);
  };

  const handleConfirmPasswordChange = (e) => {
    const confirm = e.target.value;
    setConfirmPassword(confirm);
    setDoPasswordsMatch(newPassword === confirm);
  };

  const calculatePasswordStrength = (password) => {
    let score = 0;
  
    // Criteria 1: Length
    if (password.length >= 10) score++; // Minimum acceptable length
    if (password.length >= 13) score++; // Bonus for stronger length
    if (password.length >= 16) score++; // Extra bonus for long passwords
  
    // Criteria 2: Character Variety
    const hasLowercase = /[a-z]/.test(password);
    const hasUppercase = /[A-Z]/.test(password);
    const hasNumber = /\d/.test(password);
    const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(password);
  
    if (hasLowercase) score++;
    if (hasUppercase) score++;
    if (hasNumber) score++;
    if (hasSpecialChar) score++;
  
    // Penalize predictable patterns (optional)
    const isCommonPattern = /^(123456|password|qwerty|abcdef|letmein|iloveyou)$/i.test(password);
    if (isCommonPattern) score = Math.max(score - 2, 0); // Deduct points for common patterns
  
    // Cap the score at 5
    score = Math.min(score, 5);
  
    // Map score to descriptive labels
    const strengthLabels = ["Very Weak", "Weak", "Moderate", "Strong", "Very Strong"];
    return {
      score,
      label: strengthLabels[score - 1] || "Very Weak",
    };
  };
  
  const strengthStyles = {
    "Very Weak": { color: "red" },
    Weak: { color: "orange" },
    Moderate: { color: "yellow" },
    Strong: { color: "lightgreen" },
    "Very Strong": { color: "green" },
  };

  const eyeIcons = {
    open: (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        style={{ width: "20px", height: "20px" }}
      >
        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
        <circle cx="12" cy="12" r="3" fill="black" />
      </svg>
    ),
    closed: (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        style={{ width: "20px", height: "20px" }}
      >
        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
        <circle cx="12" cy="12" r="3" fill="black" />
        <line x1="4" y1="4" x2="20" y2="20" />
      </svg>
    ),
  };

return (
  <div className="min-h-screen flex items-center justify-center px-4 bg-gray-50">
    <div className="bg-white shadow-lg rounded-lg p-8 max-w-md w-full">
      <h1 className="text-2xl font-bold text-center mb-4 text-purple-700">
        {isNewUser ? "🎉 Welcome to Proactr!" : "🔑 It's the Reset Your Password Page"}
      </h1>

      {error && <p className="text-red-600 text-sm mb-4 text-center">{error}</p>}
      {successMessage && <p className="text-green-600 text-sm mb-4 text-center">{successMessage}</p>}

      {isValid ? (
        <>
          <p className="text-sm text-gray-600 mb-6 text-center">
            {isNewUser
              ? "Welcome to the beginning of the rest of your life! Let's get your password set up."
              : "Shhh, it's a secret. Don't tell anyone, but you can enter your new password below:"}
          </p>

          {/* New Password Input */}
          <div className="relative mb-4">
            <input
              type={isPasswordVisible ? "text" : "password"}
              name="new-password"
              autoComplete="new-password"
              value={newPassword}
              onChange={handlePasswordChange}
              placeholder="New Password"
              className="w-full border border-gray-300 rounded px-4 py-2 pr-10 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
            />
            <span
              className="absolute right-3 top-2.5 cursor-pointer text-gray-500"
              onClick={() => setIsPasswordVisible(!isPasswordVisible)}
            >
              {isPasswordVisible ? eyeIcons.open : eyeIcons.closed}
            </span>
          </div>

          {/* Confirm Password Input */}
          <div className="relative mb-4">
            <input
              type={isConfirmPasswordVisible ? "text" : "password"}
              name="confirm-password"
              autoComplete="new-password"
              value={confirmPassword}
              onChange={handleConfirmPasswordChange}
              placeholder="Confirm New Password"
              className="w-full border border-gray-300 rounded px-4 py-2 pr-10 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
            />
            <span
              className="absolute right-3 top-2.5 cursor-pointer text-gray-500"
              onClick={() => setIsConfirmPasswordVisible(!isConfirmPasswordVisible)}
            >
              {isConfirmPasswordVisible ? eyeIcons.open : eyeIcons.closed}
            </span>
            <span
              className={`absolute bottom-[-6px] left-3 h-[4px] w-4 rounded-full ${
                doPasswordsMatch ? 'bg-green-500' : 'bg-red-500'
              }`}
            ></span>
          </div>

          {/* Password requirements info */}
          <p className="text-xs text-gray-500 mt-2">
            Minimum password length: 8 characters
          </p>

          {/* Strength Indicator */}
          <p className={`text-xs font-medium mt-2 mb-6 ${
            strengthStyles[passwordStrength.label] || 'text-gray-600'
          }`}>
            Password Strength: {passwordStrength.label}
          </p>

          <button
            onClick={handlePasswordReset}
            className="w-full bg-purple-700 hover:bg-purple-800 text-white font-medium py-2 px-4 rounded transition"
          >
            {isNewUser ? "Set My Password 🚀" : "Reset Password 🔄"}
          </button>
        </>
      ) : (
        <p className="text-sm text-gray-600 mt-6 text-center">
          If this link is invalid, please request a new password reset.
        </p>
      )}
    </div>
  </div>
);

}

export default ResetPassword;
